import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Edit } from "react-feather";
import DataTable from "../../../components/common/DataTable";
import { confirmDelete } from "../../../components/common/ConfirmDialog";
import { useMasterPermissions } from "../useMasterPermissions";
import { useGetDepartmentsQuery } from "../departments/departmentsApi";
import {
  useGetHrDesignationsQuery,
  useCreateHrDesignationMutation,
  useUpdateHrDesignationMutation,
  useDeleteHrDesignationMutation,
} from "./hrDesignationsApi";

const CATEGORY = { GUARD: 1, ALARMS: 2, FIELD: 3 };

// Mirrors GUI/Master/Frm_Designations.cs, now against the live
// /api/v1/masters/designations endpoints (handoff §9.2). This is the HR
// org-chart job title (Department + Guards/Alarms/Field Staff category) -
// a different entity from the RBAC Designations under Administration.
// Reading requires Designations.View (route-gated); writes gated by
// Designations.Create/.Edit + an all-branch caller (§13.3).
//
// Category (catId) is NOT restricted to a specific department - an earlier
// version of this page hid the selector unless departmentCode === 1, on the
// mistaken assumption department code 1 was "Operations" (it isn't - that
// numbering is branch-local and doesn't hold in the merged TexasCentral
// data; confirmed live, department 1 is actually "ASST ADMINISTRATOR",
// "OPERATION" is department 7). The API doc (§9.2) documents catId as a
// plain optional field with no department rule, so the selector is always
// shown now, with an explicit "None" option since most departments won't
// use it.
const HrDesignationsPage = () => {
  const { canCreate, canEdit } = useMasterPermissions("Designations");
  const { data: records = [], isLoading } = useGetHrDesignationsQuery();
  const { data: departments = [] } = useGetDepartmentsQuery();
  const [createHrDesignation, { isLoading: isCreating }] = useCreateHrDesignationMutation();
  const [updateHrDesignation, { isLoading: isUpdating }] = useUpdateHrDesignationMutation();
  const [deleteHrDesignation] = useDeleteHrDesignationMutation();

  const [mode, setMode] = useState("new");
  const [selectedCode, setSelectedCode] = useState(null);
  const [name, setName] = useState("");
  const [departmentCode, setDepartmentCode] = useState("");
  const [categoryId, setCategoryId] = useState(null);

  const selectedRecord = records.find((r) => r.designationCode === selectedCode) || null;

  useEffect(() => {
    if (mode === "new") {
      setName("");
      setDepartmentCode("");
      setCategoryId(null);
    } else {
      setName(selectedRecord?.designationName || "");
      setDepartmentCode(selectedRecord?.departmentId ?? "");
      setCategoryId(selectedRecord?.catId ?? null);
    }
  }, [mode, selectedRecord]);

  const isReadOnly = mode === "view";
  const canWrite = mode === "new" ? canCreate : canEdit;

  const select = (code) => {
    setSelectedCode(code);
    setMode("view");
  };
  const startEdit = () => setMode("edit");
  const startNew = () => {
    setSelectedCode(null);
    setMode("new");
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    const body = {
      designationName: name.trim(),
      departmentId: departmentCode ? Number(departmentCode) : null,
      catId: categoryId,
    };
    try {
      if (mode === "edit" && selectedCode != null) {
        await updateHrDesignation({ code: selectedCode, ...body }).unwrap();
      } else {
        await createHrDesignation(body).unwrap();
      }
      toast.success("Designation saved");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not save designation");
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord) return;
    const confirmed = await confirmDelete({ title: `Delete ${selectedRecord.designationName}?` });
    if (!confirmed) return;
    try {
      await deleteHrDesignation(selectedRecord.designationCode).unwrap();
      toast.success("Designation deleted");
      startNew();
    } catch (error) {
      toast.error(error?.message || "Could not delete designation");
    }
  };

  const columns = [
    { title: "Designation", dataIndex: "designationName" },
    { title: "Department", dataIndex: "departmentName", render: (v) => v || "-" },
    {
      title: "",
      dataIndex: "actions",
      width: 60,
      render: (_, record) => (
        <button type="button" className="btn btn-link p-1" onClick={() => select(record.designationCode)}>
          <Edit size={14} />
        </button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Designations</h4>
              <h6>HR master data - job titles by department</h6>
            </div>
          </div>
        </div>

        {!canCreate && !canEdit && (
          <div className="alert alert-info">
            You have read-only access. Managing designations requires Designations.Create/.Edit
            and an all-branch administrator.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Details</h5>
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Code</label>
                <input
                  type="text"
                  className="form-control"
                  value={mode === "new" ? "Auto-assigned" : selectedCode ?? ""}
                  readOnly
                  disabled
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  readOnly={isReadOnly || !canWrite}
                  disabled={isReadOnly || !canWrite}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Department</label>
                <select
                  className="form-control"
                  value={departmentCode}
                  onChange={(e) => setDepartmentCode(e.target.value)}
                  disabled={isReadOnly || !canWrite}
                >
                  <option value="">--Select--</option>
                  {departments.map((d) => (
                    <option key={d.departmentCode} value={d.departmentCode}>
                      {d.departmentName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-12 mb-3">
                <fieldset className="border rounded p-2" disabled={isReadOnly || !canWrite}>
                  <legend className="w-auto px-2 fs-6 mb-0">Category</legend>
                  <div className="d-flex gap-4">
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="cat-none"
                        name="category"
                        checked={categoryId == null}
                        onChange={() => setCategoryId(null)}
                      />
                      <label className="form-check-label" htmlFor="cat-none">None</label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="cat-guard"
                        name="category"
                        checked={categoryId === CATEGORY.GUARD}
                        onChange={() => setCategoryId(CATEGORY.GUARD)}
                      />
                      <label className="form-check-label" htmlFor="cat-guard">Guards</label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="cat-alarms"
                        name="category"
                        checked={categoryId === CATEGORY.ALARMS}
                        onChange={() => setCategoryId(CATEGORY.ALARMS)}
                      />
                      <label className="form-check-label" htmlFor="cat-alarms">Alarms</label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="cat-field"
                        name="category"
                        checked={categoryId === CATEGORY.FIELD}
                        onChange={() => setCategoryId(CATEGORY.FIELD)}
                      />
                      <label className="form-check-label" htmlFor="cat-field">Field Staff</label>
                    </div>
                  </div>
                </fieldset>
              </div>
            </div>
            {(canCreate || canEdit) && (
              <div className="d-flex gap-2">
                {mode !== "view" && canWrite && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={isCreating || isUpdating}
                  >
                    Save
                  </button>
                )}
                {mode === "view" && canEdit && (
                  <button type="button" className="btn btn-outline-primary" onClick={startEdit}>
                    Edit
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={startNew}>
                  Clear
                </button>
                {mode === "view" && canEdit && (
                  <button type="button" className="btn btn-outline-danger" onClick={handleDelete}>
                    Delete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={records}
                rowKey="designationCode"
                loading={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HrDesignationsPage;
